// Culture-styled city centres: village / town / capital for each player type
// (docs/02-systems/building-styles.md). Pure functions of the style table:
// the same (style, tier) always yields the same voxels, no randomness.
//
// Every centre is built from the same vocabulary — team plinth, base course,
// wall shell, roof, banner, landmark — so a village reads as a small piece of
// the capital. Roof and banner shapes come from lookup tables keyed by the
// style's `roof` / `banner`; nothing here branches on a building kind.
//
// Team colour: the plinth slab and the banner cloth are `team` voxels (greys
// the renderer recolours to the owner), so two players of one culture differ
// by that always-visible accent, and the culture palette keeps its colours
// (a prefab with team voxels gets a 12% tint instead of the legacy 35%).
//
// Split by concern (this file is the assembly + public surface):
//   buildings_styled_kit.ts        types, builders, tier table, build context
//   buildings_styled_roofs.ts      foundation, walls, roofs (incl. the pagoda)
//   buildings_styled_landmarks.ts  per-style landmarks + banners

import {
  type CentreStyleId,
  type CentreTier,
  type Ctx,
  TIER_SPECS,
  type V,
  dedupe,
} from './buildings_styled_kit.js';
import { LANDMARKS, banners } from './buildings_styled_landmarks.js';
import { ROOFS, foundation, walls } from './buildings_styled_roofs.js';
import { BUILDING_STYLES, type BuildingStyleId } from './styles.js';
import type { StructurePrefab } from './types.js';

export { CENTRE_TIERS } from './buildings_styled_kit.js';
export type { CentreStyleId, CentreTier } from './buildings_styled_kit.js';

// --- Assembly ---------------------------------------------------------------

const STYLE_NAMES: Readonly<Record<CentreStyleId, readonly [string, string, string]>> = {
  imperial: ['Imperial Villa', 'Imperial Forum', 'Imperial Palace'],
  nordic: ['Nordic Longhouse', 'Nordic Hall', 'Nordic Great Hall'],
  eastern: ['Eastern Hall', 'Eastern Tower', 'Eastern Pagoda'],
};

export function buildCentre(style: CentreStyleId, tier: CentreTier): StructurePrefab {
  const def = BUILDING_STYLES[style];
  const c: Ctx = { style, p: def.palette, tier, spec: TIER_SPECS[tier] };
  const blocks: V[] = [...foundation(c)];
  const roof = ROOFS[def.roof](c);
  // The pagoda owns its walls; every other roof sits on the common wall shell.
  if (def.roof !== 'swept-tiers') blocks.push(...walls(c, def.roof));
  blocks.push(...roof.blocks, ...LANDMARKS[style](c, roof), ...banners(c, roof));
  return { name: STYLE_NAMES[style][tier - 1] as string, blocks: dedupe(blocks) };
}

/** Prefab id of a style variant of `baseId`: `castle@imperial`. */
export function styledId(baseId: string, style: BuildingStyleId): string {
  return style === 'neutral' ? baseId : `${baseId}@${style}`;
}

/** The base prefab each tier replaces (the existing shared city centres). */
export const CENTRE_BASE_PREFAB: Readonly<Record<CentreTier, string>> = {
  1: 'house',
  2: 'tavern',
  3: 'castle',
};
